import { DC } from "src/dataCollector/DataCollector";
import { parseDokumentRevisionssporXml, type ServitutPaategningFiling } from "src/dataCollector/parseDokumentRevisionssporXml";
import {
  fetchTingbogDokumentPdfBlob,
  fetchTingbogDokumentRevisionssporXml,
} from "src/managers/TingbogDownload";
import { bilagFigurePngs } from "src/managers/BilagImage";
import { bundleAppendixSteps, laterPaategningFilings, mergePdfBlobs, pdfFigurePage, pdfTitlePage } from "src/managers/TingbogBundlePdf";
import { renderGmlPreviewPng } from "src/managers/GmlMapPreview";

const filingCache = new Map<string, Promise<ServitutPaategningFiling[]>>();

function loadDokumentFilings(dokumentId: string): Promise<ServitutPaategningFiling[]> {
  const id = dokumentId.trim();
  if (!id) {
    return Promise.resolve([]);
  }
  const cached = filingCache.get(id);
  if (cached) {
    return cached;
  }
  const pending = fetchTingbogDokumentRevisionssporXml(id)
    .then((xml) => parseDokumentRevisionssporXml(xml))
    .catch((error) => {
      filingCache.delete(id);
      throw error;
    });
  filingCache.set(id, pending);
  return pending;
}

export function loadPaategningFilings(dokumentId: string): Promise<ServitutPaategningFiling[]> {
  return loadDokumentFilings(dokumentId).then((filings) => laterPaategningFilings(filings));
}

export type PaategningBilagFile = {
  blob: Blob;
  name: string;
  isPdf: boolean;
};

export type WithPaategningPagesOptions = {
  includePdfBilag?: boolean;
  labelPdfBilag?: (index: number) => string;
  labelGmlBilag?: (index: number) => { title: string; body: string };
};

async function blobLooksLikePdf(blob: Blob): Promise<boolean> {
  if (!blob || blob.size < 5) {
    return false;
  }
  const head = new Uint8Array(await blob.slice(0, 5).arrayBuffer());
  return head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46;
}

function bilagTitle(name: string): string {
  return name.replace(/\.(pdf|png|jpe?g|gif|webp|bmp|tiff?|xml|gml)$/i, "").trim();
}

async function fetchBilagFile(ref: string): Promise<PaategningBilagFile | null> {
  const id = `${ref || ""}`.trim();
  if (!id) {
    return null;
  }
  try {
    const result = await DC.TL.fetchServitutBilag(id);
    if (!result?.data || result.data.size < 8) {
      return null;
    }
    return {
      blob: result.data,
      name: result.name || `bilag_${id}.pdf`,
      isPdf: await blobLooksLikePdf(result.data),
    };
  } catch (error) {
    console.warn("[påtegning] bilag failed", id, error);
    return null;
  }
}

type DocumentBilagRef = { ref: string; gml: boolean };

function documentBilagRefs(xml: Document): DocumentBilagRef[] {
  const samling = xml.getElementsByTagName("BilagReferenceSamling")[0];
  if (!samling) {
    return [];
  }
  const refs: DocumentBilagRef[] = [];
  const seen = new Set<string>();
  Array.from(samling.children).forEach((entry) => {
    const ref = (entry.getElementsByTagName("BilagReference")[0]?.textContent || "").trim();
    const key = ref.toLowerCase();
    if (!ref || seen.has(key)) {
      return;
    }
    seen.add(key);
    const kode = (entry.getElementsByTagName("BilagReferenceKode")[0]?.textContent || "").trim();
    const beskrivelse = (entry.getElementsByTagName("Afsnit")[0]?.textContent || "").trim();
    refs.push({
      ref,
      gml: kode === "38" || `${kode} ${beskrivelse}`.toLowerCase().includes("gml"),
    });
  });
  return refs;
}

async function loadDocumentBilagRefs(dokumentId: string): Promise<DocumentBilagRef[]> {
  try {
    const xml = await DC.TL.DokumentTingbog(dokumentId);
    return documentBilagRefs(xml);
  } catch (error) {
    console.warn("[påtegning] dokument bilag failed", dokumentId, error);
    return [];
  }
}

/** Named bilag/rids on later påtegninger, in filing order. Non-PDF files are included. */
export async function loadPaategningBilagFiles(
  dokumentId: string,
  signal?: AbortSignal,
): Promise<PaategningBilagFile[]> {
  const id = dokumentId.trim();
  if (!id) {
    return [];
  }
  let filings: ServitutPaategningFiling[] = [];
  try {
    filings = await loadPaategningFilings(id);
  } catch (error) {
    console.warn("[påtegning] revisionsspor failed", id, error);
    return [];
  }
  const files: PaategningBilagFile[] = [];
  for (const filing of filings) {
    for (const ref of filing.bilagRefs) {
      if (signal?.aborted) {
        return files;
      }
      const file = await fetchBilagFile(ref);
      if (file) {
        files.push(file);
      }
    }
  }
  return files;
}

/** Append each later påtegning PDF. PDF bilag are optional and meant for the one-file export. */
export async function withPaategningPages(
  mainPdf: Blob,
  dokumentId: string,
  signal?: AbortSignal,
  options?: WithPaategningPagesOptions,
): Promise<Blob> {
  const id = dokumentId.trim();
  if (!id) {
    return mainPdf;
  }
  let filings: ServitutPaategningFiling[] = [];
  try {
    filings = await loadDokumentFilings(id);
  } catch (error) {
    console.warn("[påtegning] revisionsspor failed", id, error);
  }
  const extras: Blob[] = [];
  let bilagIndex = 0;
  const seenBilag = new Set<string>();
  const appendBilag = async (ref: string, gmlHint: boolean) => {
    const key = ref.trim().toLowerCase();
    if (!key || seenBilag.has(key)) {
      return;
    }
    seenBilag.add(key);
    const file = await fetchBilagFile(ref);
    if (!file) {
      return;
    }
    bilagIndex += 1;
    const caption = options?.labelPdfBilag?.(bilagIndex) || `Bilag ${bilagIndex}`;
    if (file.isPdf) {
      if (options?.labelPdfBilag) {
        extras.push(await pdfTitlePage(caption));
      }
      extras.push(file.blob);
      return;
    }
    if (!gmlHint && !/\.gml$/i.test(file.name)) {
      const images = await bilagFigurePngs(file.blob);
      if (images?.length) {
        const note = bilagTitle(file.name);
        for (const image of images) {
          extras.push(await pdfFigurePage(caption, note, image));
        }
        return;
      }
    }
    const gmlText = await file.blob.text();
    const looksLikeGml = gmlHint || /\.gml$/i.test(file.name) || /gml|posList|LinearRing/i.test(gmlText.slice(0, 1500));
    if (!looksLikeGml) {
      bilagIndex -= 1;
      return;
    }
    const label = options?.labelGmlBilag?.(bilagIndex) || {
      title: `Bilag ${bilagIndex}, GML`,
      body: "",
    };
    const image = await renderGmlPreviewPng(gmlText);
    extras.push(await pdfFigurePage(label.title, label.body, image));
  };

  const documentBilag = options?.includePdfBilag ? await loadDocumentBilagRefs(id) : [];
  for (const step of bundleAppendixSteps(filings, documentBilag, { includeBilag: Boolean(options?.includePdfBilag) })) {
    if (signal?.aborted) {
      break;
    }
    if (step.kind === "bilag") {
      await appendBilag(step.ref, step.gml);
      continue;
    }
    try {
      const blob = await fetchTingbogDokumentPdfBlob(id, step.filingId, signal);
      if (blob.size > 0) {
        extras.push(blob);
      }
    } catch (error) {
      console.warn("[påtegning] PDF failed", step.filingId, error);
    }
  }
  if (!extras.length) {
    return mainPdf;
  }
  return mergePdfBlobs([mainPdf, ...extras]);
}
